import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProjectStore } from '../../hooks/useProjectStore';
import { aiAPI, AIMessage, type AIContextStatus, type AIConversation, type AIDocument } from '../../services/api';
import { readCompletionAIContext } from '../../services/completionAIContext';
import { readEngineeringAIContext } from '../../services/engineeringAIContext';
import { useLangStore, useT } from '../../i18n';
import AgentPlanCard from './AgentPlanCard';
import { catalogForModel, createPlan, executeStep, sanitizeSteps, stepNeedsConfirmation, type AgentPlan, type AgentStep } from '../../services/agent/executor';
import { describeCurrentPage } from '../../services/agent/registry';
import { AGENT_TEMPLATES, isTemplateInput, parseTemplate, type PlannedStep } from '../../services/agent/templates';

// 消息可以挂一张操作计划卡片 (仅前端展示, 不随请求发给模型)
type PanelMessage = AIMessage & { planId?: string };
const apiMessages = (list: PanelMessage[]): AIMessage[] =>
  list.filter((m) => m.role !== 'system').map(({ role, content }) => ({ role, content }));
type PanelMode = 'chat' | 'agent';

// ═══════════════════════════════════════════════
// 「爱压 AI」—— 全局智能顾问, 浮动按钮 + 右侧抽屉, 任何页面都能用
//
// 名字是个双关: 「爱压」谐音 AI, 「压」取自压裂。但光有「爱压」两个字看不出这是
// 大模型, 所以对外一律带上 AI 一起出现 —— 按钮上是「爱压」+ 一个 AI 角标,
// 抽屉标题是「爱压 AI」, 消息署名也是。名字要好记, 但不能让人猜它是什么。
// (从 FracturePage 抽出来, 挂到 AppLayout)
// ═══════════════════════════════════════════════

const TH = {
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  headerBorder: '#D6DAE0', statBorder: '#ECEEF1', panelHeaderBg: '#F4F6F9',
  accent: '#6A4C93',
};

const AI_BUTTON_SIZE = 56;
const AI_BUTTON_MARGIN = 28;
const AI_BUTTON_POSITION_KEY = 'fracsight_ai_button_position';

type ButtonPosition = { x: number; y: number };

const clampButtonPosition = (position: ButtonPosition): ButtonPosition => ({
  x: Math.max(8, Math.min(position.x, window.innerWidth - AI_BUTTON_SIZE - 8)),
  y: Math.max(8, Math.min(position.y, window.innerHeight - AI_BUTTON_SIZE - 8)),
});

const loadButtonPosition = (): ButtonPosition => {
  const fallback = {
    x: window.innerWidth - AI_BUTTON_SIZE - AI_BUTTON_MARGIN,
    y: window.innerHeight - AI_BUTTON_SIZE - AI_BUTTON_MARGIN,
  };
  try {
    const stored = JSON.parse(localStorage.getItem(AI_BUTTON_POSITION_KEY) || 'null');
    if (Number.isFinite(stored?.x) && Number.isFinite(stored?.y)) {
      return clampButtonPosition(stored);
    }
  } catch {
    // Ignore invalid positions from older builds.
  }
  return clampButtonPosition(fallback);
};

// 历史列表里标注"在哪问的", 只认模块级前缀, 细到子页没必要
const PAGE_LABELS: Array<[string, string]> = [
  ['/fracture/wellbore', '完井与射孔'], ['/fracture/builder', '分段分簇'],
  ['/fracture/schedule', '泵注程序'], ['/fracture/designs', '方案总览'],
  ['/fracture/new-design', '新建方案'], ['/fracture', '压裂设计'],
  ['/fracsim', '压裂模拟'], ['/formation', '地质建模'], ['/sweetspot', '甜点评价'],
  ['/mechanics', '地质力学'], ['/stability', '井壁稳定'], ['/datahub', '数据中心'],
  ['/dashboard', '仪表盘'],
];
const pageLabel = (path: string) => PAGE_LABELS.find(([prefix]) => path.startsWith(prefix))?.[1] ?? '其他页面';

const formatTime = (iso: string) => {
  // 后端返回 "YYYY-MM-DD HH:mm:ss", Safari 不认空格分隔
  const d = new Date(iso.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// 按当前路由变换快捷提问
function buildQuick(path: string): string[] {
  if (path.startsWith('/fracture/wellbore')) {
    return [
      '解释整井完井与射孔设计',
      '哪些压裂段需要人工复核?',
      '第1段为什么这样设计?',
      '射孔簇数如何沿用分段分簇方案?',
    ];
  }
  if (path.startsWith('/fracture/builder') || path.startsWith('/fracture/designs') || path.startsWith('/fracture/new-design')) {
    return [
      '解释当前智能设计方案',
      '类比案例为什么适合当前井?',
      '第1段为什么这样设计?',
      '当前方案有哪些风险需要复核?',
    ];
  }
  if (path.startsWith('/fracture/schedule')) {
    return [
      '解释当前泵注程序设计',
      '液量和砂量是否匹配?',
      '第1段泵注程序有什么风险?',
      '哪些参数需要施工前确认?',
    ];
  }
  if (path.startsWith('/formation')) {
    return [
      '帮我看这套地层的可压性',
      '目标层定哪一层比较合理?',
      '相邻层应力差够不够形成阻挡层?',
      '这套地层最适合的射孔位置',
    ];
  }
  if (path.startsWith('/sweetspot')) {
    return [
      '甜点分级的依据是什么?',
      '哪些段适合优先压裂?',
      'I/II 类段的设计差异',
      '可压性偏低怎么补救?',
    ];
  }
  if (path.startsWith('/fracture')) {
    return [
      '评价当前方案合理性',
      '段距和簇数有没有优化空间?',
      '排查砂堵 / 早期脱砂风险',
      '泵注程序怎么改善缝长?',
    ];
  }
  if (path.startsWith('/fracsim')) {
    return [
      '模拟出的缝长偏短怎么调整?',
      '液效低怎么办?',
      '缝高是否被层应力有效阻挡?',
      '导流能力够不够支撑产能?',
    ];
  }
  return [
    '这口井适合做几段?',
    '推荐段长和簇数',
    '滑溜水+线性胶组合的适用条件',
    '页岩气和致密油的设计差异',
  ];
}

export default function AIPanel() {
  const { currentProject } = useProjectStore();
  const location = useLocation();
  const navigate = useNavigate();
  const projectId = currentProject?.id ?? null;
  const t = useT();
  const lang = useLangStore((s) => s.lang);

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [buttonPosition, setButtonPosition] = useState<ButtonPosition>(loadButtonPosition);
  const [draggingButton, setDraggingButton] = useState(false);
  const [msgs, setMsgs] = useState<PanelMessage[]>([
    // 名字保留「爱压 AI」—— 产品定名, 合并时不要改回通用称呼。
    { role: 'assistant', content: '我是爱压 AI,FracSight 的压裂设计智能顾问。对话会结合当前项目数据库、工程方案与页面上下文进行分析,直接问我即可。' },
  ]);
  // ⚠️ 刻意不显示基座模型名与平台 —— 对外只呈现能力, 不暴露供应商。
  //    这是产品决定, 不是遗漏; 曾被改成显示 model+provider, 已还原。
  //    需要排查配置时看 /api/v1/ai/health/ (仅管理员)。
  const [aiReady, setAiReady] = useState<boolean | null>(null);
  // 数据状态条: 不依赖模型自觉, 直接问后端"这个项目有多少数据进了上下文"
  const [ctx, setCtx] = useState<AIContextStatus | null>(null);
  // 方案 / 邻井上下文, 供 explainDesign 定位到具体设计
  const [designContextId, setDesignContextId] = useState<number | undefined>();
  const [neighborContextId, setNeighborContextId] = useState<number | undefined>();
  const conversationScope = `${projectId}:${location.pathname}`;
  const conversationScopeRef = useRef(conversationScope);
  conversationScopeRef.current = conversationScope;
  // 历史对话: 每个页面默认开一段新对话, 第一次提问成功后才在后端建档
  const [conversationId, setConversationIdState] = useState<number | null>(null);
  const conversationIdRef = useRef<number | null>(null);
  // ref 供异步存档链读取最新值, 与 state 同步写, 不在渲染期赋值
  const setConversationId = (id: number | null) => {
    conversationIdRef.current = id;
    setConversationIdState(id);
  };
  const persistChainRef = useRef<Promise<void>>(Promise.resolve());
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<AIConversation[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [renaming, setRenaming] = useState<{ id: number; title: string } | null>(null);
  // 操作模式: 计划按 id 存, 消息里只挂 planId; ref 给异步执行链读最新值
  const [mode, setMode] = useState<PanelMode>('chat');
  const [plans, setPlans] = useState<Record<string, AgentPlan>>({});
  const plansRef = useRef<Record<string, AgentPlan>>({});
  const runningPlansRef = useRef(new Set<string>());
  // 操作计划自己跳转页面时不清空对话, 否则跳过去计划就没了
  const keepConversationOnNavRef = useRef(false);
  useEffect(() => {
    if (keepConversationOnNavRef.current) {
      keepConversationOnNavRef.current = false;
      return;
    }
    setMsgs([]);
    setInput('');
    setDesignContextId(undefined);
    setNeighborContextId(undefined);
    setConversationId(null);
    setHistoryOpen(false);
  }, [conversationScope]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // 参考文档: 地质报告 PDF、邻井总结 Word 这类数据中心收不了的东西。
  // 勾上的会随提问一起送给模型; 抽不到文本的 (扫描件/图片) 不给勾。
  const [docs, setDocs] = useState<AIDocument[]>([]);
  const [picked, setPicked] = useState<number[]>([]);
  const [docsOpen, setDocsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadDocs = () => {
    if (!projectId) { setDocs([]); return; }
    aiAPI.listDocuments(projectId)
      .then(({ data }) => setDocs(Array.isArray(data) ? data : []))
      .catch(() => setDocs([]));
  };
  useEffect(() => { if (open) loadDocs(); }, [open, projectId]);

  // 每次打开抽屉、换项目、或传完文档都重新拉一次 —— 状态条必须跟着数据走
  const loadCtx = () => {
    aiAPI.contextStatus(projectId)
      .then(({ data }) => setCtx(data))
      .catch(() => setCtx(null));
  };
  useEffect(() => { if (open) loadCtx(); }, [open, projectId, docs.length]);

  const upload = async (file: File) => {
    if (!projectId) return;
    setUploading(true);
    try {
      const { data } = await aiAPI.uploadDocument(projectId, file);
      setDocs((prev) => [data, ...prev]);
      // 抽到文本的自动勾上; 抽不到的只提示, 不勾 —— 勾了也没内容可喂
      if (data.usable) setPicked((prev) => [...prev, data.id]);
      setMsgs((prev) => [...prev, {
        role: 'assistant',
        content: data.usable
          ? t('已读取《{name}》,共 {n} 字,提问时会作为参考资料一并发给模型。', { name: data.name, n: data.char_count })
          : t('《{name}》已保存,但{reason}。它不会被发给模型。', { name: data.name, reason: data.extract_error || t('没能读出文字') }),
      }]);
    } catch (err: any) {
      const detail = err?.response?.data?.file || err?.response?.data?.detail || t('上传失败');
      setMsgs((prev) => [...prev, { role: 'assistant', content: `⚠ ${detail}` }]);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeDoc = async (doc: AIDocument) => {
    try {
      await aiAPI.deleteDocument(doc.id);
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
      setPicked((prev) => prev.filter((id) => id !== doc.id));
    } catch { /* 删不掉就留着, 不值得打断对话 */ }
  };

const buttonPositionRef = useRef(buttonPosition);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);

  // 拉一次 AI 网关配置 (模型名/平台), 打开抽屉时再拉一次保险
  useEffect(() => {
    aiAPI.info().then(({ data }) => {
      setAiReady(Boolean(data.configured));
    }).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, busy, open]);

  const [autoQuestion, setAutoQuestion] = useState<{ prompt: string; projectId: number } | null>(null);
  useEffect(() => {
    const openFromPage = (event: Event) => {
      const detail = (event as CustomEvent<{ prompt?: string; designId?: number; neighborDesignId?: number; autoSend?: boolean; projectId?: number }>).detail;
      setOpen(true);
      if (detail?.prompt) setInput(detail.prompt);
      setDesignContextId(detail?.designId);
      setNeighborContextId(detail?.neighborDesignId);
      if (detail?.autoSend && detail.prompt && detail.projectId) setAutoQuestion({ prompt: detail.prompt, projectId: detail.projectId });
    };
    window.addEventListener('fracsight-ai-open', openFromPage);
    return () => window.removeEventListener('fracsight-ai-open', openFromPage);
  }, []);

  useEffect(() => {
    const keepInsideViewport = () => {
      const clamped = clampButtonPosition(buttonPositionRef.current);
      buttonPositionRef.current = clamped;
      setButtonPosition(clamped);
    };
    window.addEventListener('resize', keepInsideViewport);
    return () => window.removeEventListener('resize', keepInsideViewport);
  }, []);

  const beginButtonDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: buttonPositionRef.current.x,
      originY: buttonPositionRef.current.y,
      moved: false,
    };
    setDraggingButton(true);
  };

  const moveButton = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) >= 4) drag.moved = true;
    if (drag.moved) {
      event.preventDefault();
      const next = clampButtonPosition({ x: drag.originX + dx, y: drag.originY + dy });
      buttonPositionRef.current = next;
      setButtonPosition(next);
      try {
        localStorage.setItem(AI_BUTTON_POSITION_KEY, JSON.stringify(next));
      } catch {
        // Keep the drag interaction available without persistent storage.
      }
    }
  };

  const finishButtonDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    suppressClickRef.current = drag.moved;
    dragRef.current = null;
    setDraggingButton(false);
    const clamped = clampButtonPosition(buttonPositionRef.current);
    buttonPositionRef.current = clamped;
    setButtonPosition(clamped);
    try {
      localStorage.setItem(AI_BUTTON_POSITION_KEY, JSON.stringify(clamped));
    } catch {
      // Dragging still works when browser storage is unavailable.
    }
  };

  // 存档失败不打断对话 —— 回复已经显示出来了, 丢的只是历史记录
  const persistExchange = (exchange: AIMessage[], page: string) => {
    persistChainRef.current = persistChainRef.current.then(async () => {
      try {
        let id = conversationIdRef.current;
        if (id === null) {
          const { data } = await aiAPI.createConversation({ project: projectId, page });
          id = data.id;
          setConversationId(id);
        }
        await aiAPI.appendConversationMessages(id, exchange);
      } catch {
        // 后端未部署对话接口或网络抖动时静默跳过
      }
    });
  };

  const loadHistory = () => {
    setHistoryLoading(true);
    setHistoryError('');
    aiAPI.listConversations(projectId)
      .then(({ data }) => setHistory(Array.isArray(data) ? data : []))
      .catch(() => { setHistory([]); setHistoryError(t('历史对话加载失败,请稍后重试。')); })
      .finally(() => setHistoryLoading(false));
  };

  const toggleHistory = () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next) loadHistory();
  };

  const startNewConversation = () => {
    if (busy) return;
    setMsgs([]);
    setInput('');
    setConversationId(null);
    setHistoryOpen(false);
  };

  const openConversation = async (id: number) => {
    if (busy) return;
    const requestScope = conversationScope;
    try {
      const { data } = await aiAPI.getConversation(id);
      if (requestScope !== conversationScopeRef.current) return;
      setMsgs(data.messages.map((m) => ({ role: m.role, content: m.content })));
      setConversationId(data.id);
      setHistoryOpen(false);
    } catch {
      setHistoryError(t('该对话无法打开,可能已被删除。'));
    }
  };

  const removeConversation = async (id: number) => {
    if (!window.confirm(t('删除这段对话?删除后无法恢复。'))) return;
    try {
      await aiAPI.deleteConversation(id);
      setHistory((prev) => prev.filter((c) => c.id !== id));
      if (conversationIdRef.current === id) {
        setConversationId(null);
        setMsgs([]);
      }
    } catch {
      setHistoryError(t('删除失败,请稍后重试。'));
    }
  };

  const saveRename = async () => {
    if (!renaming) return;
    const title = renaming.title.trim().slice(0, 120);
    setRenaming(null);
    if (!title) return;
    try {
      const { data } = await aiAPI.renameConversation(renaming.id, title);
      setHistory((prev) => prev.map((c) => (c.id === data.id ? { ...c, title: data.title } : c)));
    } catch {
      setHistoryError(t('重命名失败,请稍后重试。'));
    }
  };

  const updatePlan = (planId: string, change: (plan: AgentPlan) => AgentPlan) => {
    const current = plansRef.current[planId];
    if (!current) return;
    plansRef.current = { ...plansRef.current, [planId]: change(current) };
    setPlans(plansRef.current);
  };
  const updateStep = (planId: string, key: string, patch: Partial<AgentStep>) =>
    updatePlan(planId, (plan) => ({ ...plan, steps: plan.steps.map((s) => (s.key === key ? { ...s, ...patch } : s)) }));

  const stopPlan = (planId: string) => updatePlan(planId, (plan) => ({
    ...plan,
    stopped: true,
    steps: plan.steps.map((s) => (s.status === 'pending' || s.status === 'awaiting' ? { ...s, status: 'skipped' } : s)),
  }));

  const agentNavigate = (path: string) => {
    if (window.location.pathname === path) return;
    keepConversationOnNavRef.current = true;
    navigate(path);
  };

  const executePlanStep = async (planId: string, step: AgentStep) => {
    updateStep(planId, step.key, { status: 'running', result: undefined });
    try {
      const result = await executeStep(step, { navigate: agentNavigate, translate: t });
      updateStep(planId, step.key, { status: 'done', result });
      return true;
    } catch (error: any) {
      updateStep(planId, step.key, { status: 'failed', result: error?.message || t('执行失败') });
      // 一步失败就停下, 后续步骤多半依赖它, 交给人判断
      stopPlan(planId);
      return false;
    }
  };

  // 顺序推进: 只读/跳转自动执行, 写入/运行停在"待确认"等人点
  const advancePlan = async (planId: string) => {
    if (runningPlansRef.current.has(planId)) return;
    runningPlansRef.current.add(planId);
    try {
      for (;;) {
        const plan = plansRef.current[planId];
        if (!plan || plan.stopped) break;
        const step = plan.steps.find((s) => s.status === 'pending' || s.status === 'awaiting');
        if (!step || step.status === 'awaiting') break;
        if (stepNeedsConfirmation(step)) {
          updateStep(planId, step.key, { status: 'awaiting' });
          break;
        }
        if (!(await executePlanStep(planId, step))) break;
      }
    } finally {
      runningPlansRef.current.delete(planId);
    }
  };

  const confirmPlanStep = async (planId: string, key: string) => {
    const step = plansRef.current[planId]?.steps.find((s) => s.key === key);
    if (!step || step.status !== 'awaiting') return;
    if (await executePlanStep(planId, step)) void advancePlan(planId);
  };

  const skipPlanStep = (planId: string, key: string) => {
    updateStep(planId, key, { status: 'skipped' });
    void advancePlan(planId);
  };

  const startPlan = (source: AgentPlan['source'], steps: PlannedStep[], reply: string, base: PanelMessage[], question: string) => {
    const plan = createPlan(source, steps);
    plansRef.current = { ...plansRef.current, [plan.id]: plan };
    setPlans(plansRef.current);
    setMsgs([...base, { role: 'assistant', content: reply, planId: plan.id }]);
    const summary = [reply, ...steps.map((step, i) => `${i + 1}. ${step.command}`)].filter(Boolean).join('\n');
    persistExchange([{ role: 'user', content: question }, { role: 'assistant', content: summary }], location.pathname);
    void advancePlan(plan.id);
  };

  const sendAgent = async (q: string) => {
    const base: PanelMessage[] = [...msgs, { role: 'user', content: q }];
    setInput('');

    if (isTemplateInput(q)) {
      const parsed = parseTemplate(q);
      if (parsed.ok) {
        startPlan('template', parsed.steps, t('按命令模板执行:'), base, q);
      } else {
        const content = parsed.error === 'help'
          ? [t('可用命令模板:'), ...AGENT_TEMPLATES.map((item) => `${item.usage}  —  ${t(item.description)}`)].join('\n')
          : `⚠ ${t(parsed.error, parsed.vars)}`;
        setMsgs([...base, { role: 'assistant', content }]);
      }
      return;
    }

    setMsgs(base);
    setBusy(true);
    try {
      const { data } = await aiAPI.agentPlan({
        message: q,
        catalog: catalogForModel(),
        history: apiMessages(msgs).slice(-8),
        page: location.pathname,
        page_state: describeCurrentPage(),
        project_id: projectId,
        language: lang,
      });
      const { steps, rejected } = sanitizeSteps(data.actions);
      const dropped = [...(data.rejected ?? []), ...rejected];
      const reply = [data.reply, dropped.length ? t('(已忽略无法识别的命令: {list})', { list: dropped.join(', ') }) : '']
        .filter(Boolean).join('\n');
      if (steps.length) startPlan('llm', steps, reply, base, q);
      else {
        const content = reply || t('(无回复)');
        setMsgs([...base, { role: 'assistant', content }]);
        persistExchange([{ role: 'user', content: q }, { role: 'assistant', content }], location.pathname);
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || t('调用失败');
      setMsgs([...base, { role: 'assistant', content: `⚠ ${detail}\n${t('也可以直接用命令模板, 输入 /帮助 查看。')}` }]);
    } finally {
      setBusy(false);
    }
  };

  const send = async (text: string) => {
    const requestScope = conversationScope;
    const q = text.trim();
    if (!q || busy) return;
    if (mode === 'agent' || isTemplateInput(q)) {
      await sendAgent(q);
      return;
    }
    const next: PanelMessage[] = [...msgs, { role: 'user', content: q }];
    setMsgs(next);
    setInput('');
    setBusy(true);
    try {
      const useLocalDesignEngine = projectId && (
        location.pathname.startsWith('/fracture/builder') ||
        location.pathname.startsWith('/fracture/designs') ||
        location.pathname.startsWith('/fracture/new-design') ||
        location.pathname.startsWith('/fracture/schedule')
      );
      const { data } = location.pathname.startsWith('/fracture/wellbore') && projectId
        ? await aiAPI.explainCompletion({ project_id: projectId, question: q, messages: apiMessages(next), completion_context: readCompletionAIContext(projectId), language: lang })
        : useLocalDesignEngine
          ? await aiAPI.explainDesign({ project_id: projectId, question: q, design_id: designContextId, neighbor_design_id: neighborContextId, messages: apiMessages(next), engineering_context: readEngineeringAIContext(projectId, location.pathname.split('/').pop() ?? ''), language: lang })
          : await aiAPI.chat({
            messages: apiMessages(next),
            project_id: projectId ?? undefined,
            language: lang,
          });
      if (requestScope === conversationScopeRef.current) {
        const reply = data.reply || t('(无回复)');
        setMsgs([...next, { role: 'assistant', content: reply }]);
        persistExchange([{ role: 'user', content: q }, { role: 'assistant', content: reply }], location.pathname);
      }
    } catch (err: any) {
      const detail = err?.response?.status === 404 ? t('当前连接的后端缺少此接口，或所选方案已删除。请核对后端部署版本和方案，刷新页面后重试。') : err?.response?.data?.detail || err?.message || t('调用失败');
      if (requestScope === conversationScopeRef.current) setMsgs([...next, { role: 'assistant', content: `⚠ ${detail}` }]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!autoQuestion) return;
    if (autoQuestion.projectId !== projectId || !['/fracture/wellbore', '/fracture/builder', '/fracture/schedule', '/fracture/designs', '/fracture/new-design'].includes(location.pathname)) {
      setAutoQuestion(null);
      return;
    }
    if (busy) return;
    // Wait for the editor's effects to publish the newly generated state.
    const timer = window.setTimeout(() => { setAutoQuestion(null); void send(autoQuestion.prompt); }, 0);
    return () => window.clearTimeout(timer);
  }, [autoQuestion, busy, projectId, location.pathname]);

  const quick = buildQuick(location.pathname);

  // 在登录页/项目选择页隐藏
  if (location.pathname === '/login' || location.pathname === '/projects') return null;

  return (
    <>
      {/* 浮动按钮 */}
      {!open && (
        <button
          onClick={() => {
            if (suppressClickRef.current) {
              suppressClickRef.current = false;
              return;
            }
            setOpen(true);
          }}
          onPointerDown={beginButtonDrag}
          onPointerMove={moveButton}
          onPointerUp={finishButtonDrag}
          onPointerCancel={finishButtonDrag}
          title={t('爱压 AI · 压裂设计智能顾问（可拖动）')}
          aria-label={t('爱压 AI · 压裂设计智能顾问（可拖动）')}
          style={{
            position: 'fixed', left: buttonPosition.x, top: buttonPosition.y, zIndex: 9998,
            width: 56, height: 56, borderRadius: 28,
            background: 'linear-gradient(135deg,#6A4C93 0%,#2874A6 100%)',
            color: '#fff', border: 'none', cursor: draggingButton ? 'grabbing' : 'grab',
            fontSize: 13, fontWeight: 800, letterSpacing: 0.5,
            boxShadow: '0 6px 18px rgba(40,116,166,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            touchAction: 'none', userSelect: 'none',
          }}
        >
          <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: 1 }}>爱压</span>
          <span style={{
            position: 'absolute', top: 5, right: 6,
            fontSize: 8.5, fontWeight: 800, letterSpacing: 0.4, lineHeight: 1,
            padding: '2px 3.5px', borderRadius: 2.5,
            background: 'rgba(255,255,255,0.92)', color: '#2874A6',
          }}>AI</span>
        </button>
      )}

      {/* 抽屉 */}
      <div
        style={{
          position: 'fixed', top: 0, right: open ? 0 : -460, bottom: 0,
          width: 440, zIndex: 9999, background: '#FFFFFF',
          borderLeft: `1px solid ${TH.headerBorder}`,
          boxShadow: open ? '-8px 0 24px rgba(30,42,58,0.10)' : 'none',
          transition: 'right 0.25s ease', display: 'flex', flexDirection: 'column',
          fontFamily: 'inherit',
        }}
      >
        {/* 抽屉头 */}
        <div
          style={{
            padding: '12px 16px', background: TH.panelHeaderBg,
            borderBottom: `1px solid ${TH.headerBorder}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: TH.text, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>爱压</span>
              <span style={{
                fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, lineHeight: 1,
                padding: '2.5px 5px', borderRadius: 3, color: '#fff',
                background: 'linear-gradient(135deg,#6A4C93 0%,#2874A6 100%)',
              }}>AI</span>
              <span style={{ fontWeight: 400, color: TH.textSec, fontSize: 12 }}>{t('压裂设计智能顾问')}</span>
            </div>
            <div style={{ fontSize: 10, color: TH.textMuted, marginTop: 2 }}>
              {t('全局可用 · 基于当前项目数据')}
              {aiReady === false && t(' · 未配置')}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              onClick={startNewConversation}
              disabled={busy}
              title={t('开始新对话')}
              style={headerButtonStyle(false, busy)}
            >{t('＋ 新对话')}</button>
            <button
              onClick={toggleHistory}
              title={t('查看历史对话')}
              aria-expanded={historyOpen}
              style={headerButtonStyle(historyOpen, false)}
            >{t('历史')}</button>
            <button
              onClick={() => setOpen(false)}
              style={{
                border: 'none', background: 'transparent', cursor: 'pointer',
                fontSize: 20, color: TH.textSec, padding: '0 4px',
              }}
            >×</button>
          </div>
        </div>

        {/* 数据状态条 —— 不依赖模型自觉。
            模型"看起来很专业"本身就是风险面: 空项目里它也能给出一套像模像样的
            数字。提示词已经加了硬规则, 但界面上还要有一条独立于它的事实陈述。
            数据来自 /ai/context-status/, 与聊天用的是同一处判定。 */}
        {ctx && (
          <div
            style={{
              padding: '8px 14px',
              borderBottom: `1px solid ${TH.statBorder}`,
              background: ctx.has_data ? '#F4F8FB' : 'rgba(212,119,12,0.09)',
              fontSize: 11,
              lineHeight: 1.6,
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'baseline', gap: 6,
              color: ctx.has_data ? TH.textSec : '#8A4E06', fontWeight: 600,
            }}>
              <span>{ctx.has_data ? t('本次提问会用到的项目数据') : t('⚠ 当前项目尚无入库数据')}</span>
            </div>

            {ctx.has_data ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', marginTop: 3 }}>
                {ctx.counts.map((c) => (
                  <span
                    key={c.key}
                    style={{ color: c.count > 0 ? TH.textSec : TH.textMuted }}
                  >
                    {t(c.label)}
                    <b style={{
                      marginLeft: 3,
                      fontFamily: 'ui-monospace, monospace',
                      color: c.count > 0 ? TH.text : TH.textMuted,
                    }}>{c.count}</b>
                    <span style={{ color: TH.textMuted }}>{t(c.unit)}</span>
                  </span>
                ))}
              </div>
            ) : (
              <div style={{ color: '#8A4E06', marginTop: 2 }}>{t(ctx.notice)}</div>
            )}
          </div>
        )}

        {/* 历史对话 —— 覆盖在消息区上方, 选中后回到消息区继续聊 */}
        {historyOpen && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', background: '#FAFBFC' }}>
            <div style={{ fontSize: 11, color: TH.textMuted, marginBottom: 8 }}>
              {projectId ? t('项目 #{id} 的历史对话', { id: projectId }) : t('未选项目时的历史对话')}{t(' · 仅自己可见')}
            </div>
            {historyError && <div style={{ fontSize: 11.5, color: '#C2533A', marginBottom: 8 }}>{historyError}</div>}
            {historyLoading && <div style={{ fontSize: 12, color: TH.textMuted }}>{t('加载中…')}</div>}
            {!historyLoading && !historyError && history.length === 0 && (
              <div style={{ fontSize: 12, color: TH.textMuted, lineHeight: 1.7 }}>
                {t('还没有历史对话。提问并收到回复后会自动保存。')}
              </div>
            )}
            {!historyLoading && history.map((c) => {
              const current = c.id === conversationId;
              const editing = renaming?.id === c.id;
              return (
                <div
                  key={c.id}
                  onClick={() => { if (!editing) void openConversation(c.id); }}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 6,
                    padding: '7px 9px', borderRadius: 4, background: '#fff', cursor: editing ? 'default' : 'pointer',
                    border: `1px solid ${current ? TH.accent : TH.statBorder}`,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {editing ? (
                      <input
                        autoFocus
                        value={renaming.title}
                        maxLength={120}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setRenaming({ id: c.id, title: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void saveRename();
                          if (e.key === 'Escape') setRenaming(null);
                        }}
                        onBlur={() => void saveRename()}
                        style={{
                          width: '100%', boxSizing: 'border-box', fontSize: 12, padding: '2px 6px',
                          border: `1px solid ${TH.headerBorder}`, borderRadius: 3, fontFamily: 'inherit',
                        }}
                      />
                    ) : (
                      <div style={{
                        fontSize: 12, color: TH.text, fontWeight: 600,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {c.title || t('未命名对话')}
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: TH.textMuted, marginTop: 2 }}>
                      {t('{page} · {n} 条 · {time}', { page: t(pageLabel(c.page)), n: c.message_count, time: formatTime(c.updated_at) })}
                      {current && <span style={{ color: TH.accent }}>{t(' · 当前')}</span>}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setRenaming({ id: c.id, title: c.title }); }}
                    title={t('重命名')}
                    style={rowIconStyle}
                  >✎</button>
                  <button
                    onClick={(e) => { e.stopPropagation(); void removeConversation(c.id); }}
                    title={t('删除')}
                    style={rowIconStyle}
                  >×</button>
                </div>
              );
            })}
          </div>
        )}

        {/* 消息区 */}
        <div
          ref={scrollRef}
          style={{
            flex: 1, overflowY: 'auto', padding: '14px 14px 8px',
            display: historyOpen ? 'none' : 'flex', flexDirection: 'column', gap: 10,
          }}
        >
          {msgs.map((m, i) => (
            <div
              key={i}
              // 对话内容 (用户输入、模型回复) 不是界面文字, 不走页面翻译层
              data-i18n-skip=""
              style={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '88%',
                padding: '8px 12px', borderRadius: 10, fontSize: 12.5, lineHeight: 1.55,
                background: m.role === 'user' ? 'rgba(40,116,166,0.08)' : '#F4F6F9',
                color: TH.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                border: m.role === 'user' ? '1px solid rgba(40,116,166,0.18)' : `1px solid ${TH.statBorder}`,
              }}
            >
              {m.content}
            </div>
          )).flatMap((bubble, i) => {
            const plan = msgs[i].planId ? plans[msgs[i].planId!] : undefined;
            return plan ? [bubble, (
              <AgentPlanCard
                key={plan.id}
                plan={plan}
                onConfirm={(key) => void confirmPlanStep(plan.id, key)}
                onSkip={(key) => skipPlanStep(plan.id, key)}
                onStop={() => stopPlan(plan.id)}
              />
            )] : [bubble];
          })}
          {busy && (
            <div
              style={{
                alignSelf: 'flex-start', padding: '8px 12px', borderRadius: 10,
                background: '#F4F6F9', fontSize: 12, color: TH.textMuted,
              }}
            >
              {t('思考中…')}
            </div>
          )}
        </div>

        {/* 快捷问 */}
        {!busy && !historyOpen && msgs.length < 4 && (
          <div style={{ padding: '0 12px 8px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(mode === 'agent' ? AGENT_QUICK : quick).map((q, i) => (
              <button
                key={i}
                onClick={() => (mode === 'agent' && q.startsWith('/') ? setInput(q) : send(t(q)))}
                style={{
                  padding: '4px 10px', fontSize: 11, borderRadius: 14, cursor: 'pointer',
                  border: `1px solid ${TH.statBorder}`, background: '#FAFBFC', color: TH.textSec,
                }}
              >
                {t(q)}
              </button>
            ))}
          </div>
        )}

        {/* 参考文档 —— 数据中心收不了的东西 (报告 PDF / 邻井总结 Word / 施工方案) 走这里 */}
        <div style={{ borderTop: `1px solid ${TH.statBorder}`, background: '#FAFBFC' }}>
          <button
            onClick={() => setDocsOpen((v) => !v)}
            style={{
              width: '100%', padding: '7px 12px', fontSize: 11.5, cursor: 'pointer',
              border: 'none', background: 'transparent', color: TH.textSec,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <span>
              {t('📎 参考文档')}
              {picked.length > 0 && (
                <span style={{ color: TH.accent, fontWeight: 700 }}>{t(' · 已选 {n} 份', { n: picked.length })}</span>
              )}
              {docs.length > 0 && picked.length === 0 && (
                <span style={{ color: TH.textMuted }}>{t(' · {n} 份未选', { n: docs.length })}</span>
              )}
            </span>
            <span>{docsOpen ? '▾' : '▸'}</span>
          </button>

          {docsOpen && (
            <div style={{ padding: '0 12px 10px' }}>
              <input
                ref={fileRef}
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) upload(f);
                }}
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={!projectId || uploading}
                style={{
                  width: '100%', padding: '6px 10px', fontSize: 11.5, borderRadius: 4,
                  border: `1px dashed ${TH.headerBorder}`, background: '#fff',
                  color: !projectId ? TH.textMuted : TH.textSec,
                  cursor: !projectId || uploading ? 'not-allowed' : 'pointer',
                }}
              >
                {uploading ? t('读取中…') : projectId ? t('＋ 上传文档(PDF / Word / Excel / 文本 / 图片,不限格式)') : t('请先选择项目')}
              </button>

              <div style={{ fontSize: 10, color: TH.textMuted, marginTop: 5, lineHeight: 1.6 }}>
                {t('这里的文件')}<b>{t('不进数据中心、不参与计算')}</b>{t(',只作为背景资料读给模型。')}
                {t('图片与扫描件会自动转写成文字(转写结果可能与原件有出入,请核对)。')}
              </div>

              {docs.map((d) => {
                const on = picked.includes(d.id);
                return (
                  <div
                    key={d.id}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 7, marginTop: 7,
                      padding: '6px 8px', borderRadius: 4, background: '#fff',
                      border: `1px solid ${on ? TH.accent : TH.statBorder}`,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={!d.usable}
                      onChange={() => setPicked((prev) =>
                        prev.includes(d.id) ? prev.filter((x) => x !== d.id) : [...prev, d.id])}
                      style={{ marginTop: 2, cursor: d.usable ? 'pointer' : 'not-allowed' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 11.5, color: TH.text, fontWeight: 600,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {d.name}
                      </div>
                      <div style={{ fontSize: 10, color: d.usable ? TH.textMuted : '#D4770C' }}>
                        {d.usable
                          ? `${t('{n} 字', { n: d.char_count })}${d.page_count ? t(' · {n} 页', { n: d.page_count }) : ''}`
                          : d.extract_error || d.extract_status_display}
                      </div>
                    </div>
                    <button
                      onClick={() => removeDoc(d)}
                      title={t('删除')}
                      style={{
                        border: 'none', background: 'transparent', cursor: 'pointer',
                        color: TH.textMuted, fontSize: 13, lineHeight: 1, padding: 2,
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 输入区 */}
        <div style={{ padding: 12, borderTop: `1px solid ${TH.statBorder}`, background: '#FFFFFF' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            {(['chat', 'agent'] as PanelMode[]).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={mode === value}
                onClick={() => setMode(value)}
                style={headerButtonStyle(mode === value, false)}
              >
                {value === 'chat' ? t('问答') : t('操作')}
              </button>
            ))}
            <span style={{ fontSize: 10, color: TH.textMuted }}>
              {mode === 'agent' ? t('操作模式: 驱动左侧页面执行, 改动前逐步确认') : t('输入 / 使用命令模板')}
            </span>
          </div>
          {input.startsWith('/') && !input.includes(' ') && (
            <div style={{
              marginBottom: 6, maxHeight: 150, overflowY: 'auto', border: `1px solid ${TH.statBorder}`,
              borderRadius: 6, background: '#FAFBFC',
            }}>
              {AGENT_TEMPLATES.filter((item) => item.usage.startsWith(input.trim())).map((item) => (
                <button
                  key={item.usage}
                  type="button"
                  onClick={() => setInput(item.usage.split(' ')[0] + ' ')}
                  style={{
                    display: 'flex', width: '100%', gap: 8, padding: '4px 8px', border: 'none',
                    background: 'transparent', cursor: 'pointer', fontSize: 11, textAlign: 'left',
                  }}
                >
                  <span style={{ color: TH.accent, fontWeight: 700, whiteSpace: 'nowrap' }}>{item.usage}</span>
                  <span style={{ color: TH.textMuted }}>{t(item.description)}</span>
                </button>
              ))}
            </div>
          )}
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
            placeholder={mode === 'agent'
              ? t('描述要做的操作, 例如: 帮我生成泵注程序然后跑模拟 (或输入 / 命令)')
              : t('问任何压裂相关的问题…(Enter 发送 · Shift+Enter 换行)')}
            rows={3}
            style={{
              width: '100%', resize: 'none', boxSizing: 'border-box',
              fontSize: 12.5, padding: '8px 10px', borderRadius: 6,
              border: `1px solid ${TH.headerBorder}`, fontFamily: 'inherit', color: TH.text,
              outline: 'none',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
            <span style={{ fontSize: 10, color: TH.textMuted }}>
              {projectId ? t('项目 #{id} 上下文已注入', { id: projectId }) : t('未选项目')}
              {picked.length > 0 && t(' · 附 {n} 份文档', { n: picked.length })}
            </span>
            <button
              onClick={() => send(input)}
              disabled={busy || !input.trim()}
              style={{
                padding: '5px 16px', fontSize: 12, fontWeight: 600, borderRadius: 4,
                border: 'none', cursor: busy || !input.trim() ? 'not-allowed' : 'pointer',
                background: busy || !input.trim() ? '#C5C9D2' : TH.accent, color: '#fff',
              }}
            >
              {busy ? '…' : t('发送')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

const AGENT_QUICK = ['/状态', '/方案列表', '/生成泵注', '/模拟', '/帮助'];

const headerButtonStyle = (active: boolean, disabled: boolean): React.CSSProperties => ({
  padding: '3px 8px', fontSize: 11, borderRadius: 4, lineHeight: 1.4, whiteSpace: 'nowrap',
  border: `1px solid ${active ? TH.accent : TH.headerBorder}`,
  background: active ? 'rgba(106,76,147,0.08)' : '#fff',
  color: disabled ? TH.textMuted : active ? TH.accent : TH.textSec,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

const rowIconStyle: React.CSSProperties = {
  border: 'none', background: 'transparent', cursor: 'pointer',
  color: TH.textMuted, fontSize: 12, lineHeight: 1, padding: 2,
};
